/**
 * Web: open the signed-out (guest) actor database as a LOCAL-ONLY
 * `@tursodatabase/sync-wasm` `Database` — same engine as the signed-in path, so
 * web runs ONE SQLite engine in both states.
 *
 * The two hard rules this file exists to enforce:
 *
 *   1. **No `url`, no `authToken`, ever.** Omitting the url is what makes the wasm
 *      engine open a purely local database (upstream: *"if omitted - local-only
 *      database will be created"*). It needs no server, works offline from the very
 *      first launch, and opens in single-digit milliseconds.
 *
 *   2. **A separate file from `actor.db`.** `actor.db` is reserved for sync mode.
 *      Handing the sync engine a non-empty file that was never `connect()`ed
 *      produces a database that is readable, reports `push()` success, and syncs in
 *      NEITHER direction — with no error at any point. (When the remote happens to
 *      hold a same-named table it instead replaces the local content outright.)
 *      Keeping guest data in its own path makes that state unreachable by
 *      construction rather than by discipline: no code path can hand this file a
 *      url, because this function never accepts one.
 *
 * The guest→account handoff therefore moves ROWS, not the file: see
 * `carry-guest-study-through-import.ts`. Turso's `connect()` establishes sync linkage only
 * on a clean start, so a never-connected file can never be promoted in place.
 *
 * Wrapped in the same adapter stack as the sync path (`adaptTursoToExpoSqlite` +
 * `withSqliteBusyRetry` + a connection-scoped lock) so the actor data layer sees an
 * identical expo-sqlite surface either way, and drizzle transactions serialize the
 * same. The lock has no `pull()`/`push()` to exclude here, but it still serializes
 * drizzle's `begin` … `commit` against plain statements.
 */
import { adaptTursoToExpoSqlite } from "#turso-adapter";
import {
  classifyTransactionControl,
  createActorSyncConnectionLock,
} from "#actor-sync-connection-lock";
import { withSqliteBusyRetry } from "#with-sqlite-busy-retry";
import type { SQLiteDatabase } from "#open-sqlite-database";
import { loadSyncWasm } from "#load-sync-wasm";
import {
  type ActorSchemaEngine,
  type ActorSchemaUpdate,
  updateActorSchema,
} from "#update-actor-schema";
import pRetry, { AbortError } from "@-/com.npmjs.p-retry";
import { ActorForeignHistoryError } from "@-/dev.improve.haniverse.lib.database.actor.migration-tags";
import { wipeTursoDatabaseFiles } from "#wipe-turso-database-files";
import { extractMutatedTable, splitSqlStatements } from "@-/com.npmjs.expo-sqlite";

/**
 * Retry budget for the OPFS access-handle handover between two page loads.
 *
 * A same-tab navigation (`openLink`, a router hard nav, a reload) tears down the old document while
 * its sync-wasm WORKER — which is what actually owns the OPFS sync access handle on `guest.db` — is
 * still winding down. The new document then opens the same file and the browser throws:
 *
 *     Failed to execute 'createSyncAccessHandle' on 'FileSystemFileHandle': Access Handles cannot
 *     be created if there is another open Access Handle or Writable stream associated with the
 *     same file.
 *
 * Nothing in the app can prevent this from the new page's side: the stale handle belongs to a
 * document that no longer exists, so there is no object left to close, and no `pagehide` hook can be
 * relied on (the browser may kill the worker without running one, and bfcache makes the timing
 * non-deterministic). The handover is inherently racy and BRIEF — so the correct response is to wait
 * it out rather than fail the page.
 *
 * This is the `web-url-routing` staging failure: the flow does `prepare-guest-user` (page A opens
 * `guest.db`) then `openLink ${WEB_ORIGIN}/browse` (page B opens it again), and page B rendered
 * "Something went wrong" instead of Levels. Intermittent because it depends on how fast the old
 * worker is reaped.
 *
 * NOT the same bug as the sync-path leak fixed in 2ed3081fc4: that one is `actor.db` and comes from
 * a `pTimeout` abandoning an in-flight open. This flow never signs in, so it only ever touches
 * `guest.db` — which is exactly why that fix did not help here.
 */
const OPEN_MAX_ATTEMPTS = 6;
const OPEN_RETRY_DELAY_MS = 250;
const CHECKPOINT_RETRY_DELAY_MS = 50;
const CHECKPOINT_RETRIES = 100;

function isWalCheckpointBusy(rows: readonly unknown[]): boolean {
  const row = rows[0];
  if (typeof row !== "object" || row === null) {
    return false;
  }
  const busy = Reflect.get(row, "busy");
  return busy === 1 || busy === 1n;
}

/** True for the browser's exclusive-access-handle error, which is what a stale handle looks like. */
function isAccessHandleConflict(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return message.includes("Access Handles cannot be created");
}

export interface GuestTursoConnection {
  /** Close the underlying engine handle (OPFS releases its sync access handles). */
  close(): Promise<void>;
  connection: SQLiteDatabase;
  databasePath: string;
  /** Bring the database up to the given migrations, inside the connection's lock. */
  updateSchema(migrations: Readonly<Record<string, string>>): Promise<ActorSchemaUpdate>;
}

/**
 * Open the signed-out database, starting it over when its migration history is not this build's
 * (see the same wrapper in `open-actor-turso-sync-connection.web.ts`).
 */
export async function openGuestTursoConnection(
  databaseName: string,
): Promise<GuestTursoConnection> {
  try {
    return await openGuestTursoConnectionOnce(databaseName);
  } catch (error) {
    if (!(error instanceof ActorForeignHistoryError)) {
      throw error;
    }
    await wipeTursoDatabaseFiles(databaseName);
    return openGuestTursoConnectionOnce(databaseName);
  }
}

async function openGuestTursoConnectionOnce(databaseName: string): Promise<GuestTursoConnection> {
  const { Database } = await loadSyncWasm();

  // Retry ONLY the access-handle conflict (see `OPEN_MAX_ATTEMPTS`): the previous page's worker may
  // still own the OPFS handle for a few hundred ms after a same-tab navigation. Any other error is
  // rethrown immediately — a genuine corruption or wasm failure must not be masked by ~1.5s of
  // silent retrying.
  //
  // The `Database` is reconstructed per attempt. Reusing one whose `connect()` failed would retry
  // against an engine already in a bad state, and constructing it is cheap for a local-only file.
  const tursoDb = await pRetry(
    async () => {
      // NOTE: `{ path }` only. Adding `url`/`authToken` here would defeat rule (2)
      // above — the whole point of this file is that a guest database is never handed
      // a sync url.
      const database = new Database({ path: databaseName });
      try {
        // Local-only `connect()` does no network I/O; it initializes the engine handle.
        await database.connect();
      } catch (error) {
        // Drop this attempt's engine before retrying, or a partially-initialised one could hold the
        // very handle the next attempt is waiting for.
        try {
          await database.close?.();
        } catch {
          /* best-effort: the engine may already be unusable. */
        }
        if (!isAccessHandleConflict(error)) {
          throw new AbortError(error instanceof Error ? error : new Error(String(error)));
        }
        throw error;
      }
      return database;
    },
    {
      // Short, near-constant backoff: the handover resolves in a few hundred ms, so exponential
      // growth would just add dead time to a page the user is waiting on. ~5 retries x ~250ms
      // gives roughly 1.5s of grace before the error boundary — long enough for the old worker to
      // be reaped, short enough not to feel like a hang.
      factor: 1,
      maxTimeout: OPEN_RETRY_DELAY_MS,
      minTimeout: OPEN_RETRY_DELAY_MS,
      retries: OPEN_MAX_ATTEMPTS - 1,
    },
  );

  // The same updater that migrates a synced replica (see `update-actor-schema.ts`): one
  // transaction, so the database reaches this build's schema or stays where it was. Nothing else
  // holds this engine yet.
  const updateSchema = async (migrations?: Readonly<Record<string, string>>) => {
    const update = await updateActorSchema(
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- the wasm Database has the raw `all`/`exec`/`run` calls the updater makes
      tursoDb as unknown as ActorSchemaEngine,
      { migrations },
    );
    if (update.applied.length > 0) {
      // Fold the migration into the main file like every later write (see below). Best-effort:
      // a busy checkpoint only leaves it in the WAL, which is durable too.
      await tursoDb.all("PRAGMA wal_checkpoint(TRUNCATE)").catch(() => []);
    }
    return update;
  };
  try {
    await updateSchema();
  } catch (error) {
    try {
      await tursoDb.close();
    } catch {
      /* best-effort: the engine may already be unusable. */
    }
    throw error;
  }

  const lock = createActorSyncConnectionLock();
  let transactionDepth = 0;
  let transactionHasWrites = false;
  const checkpointAfterStatement = async (source: string, isScript: boolean): Promise<void> => {
    let shouldCheckpoint = false;
    const statements = isScript ? splitSqlStatements(source) : [source];
    for (const statement of statements) {
      const control = classifyTransactionControl(statement);
      if (control === "begin") {
        transactionDepth += 1;
        continue;
      }
      if (control === "rollback") {
        transactionDepth = Math.max(0, transactionDepth - 1);
        if (transactionDepth === 0) {
          transactionHasWrites = false;
        }
        continue;
      }
      if (control === "commit") {
        transactionDepth = Math.max(0, transactionDepth - 1);
        if (transactionDepth === 0 && transactionHasWrites) {
          transactionHasWrites = false;
          shouldCheckpoint = true;
        }
        continue;
      }
      if (extractMutatedTable(statement) !== null) {
        if (transactionDepth > 0) {
          transactionHasWrites = true;
        } else {
          shouldCheckpoint = true;
        }
      }
    }
    if (!shouldCheckpoint) {
      return;
    }

    // This callback runs INSIDE the adapter's connection lock. Keeping the write (or transaction
    // commit) and checkpoint in one lock slot prevents another RPC's `begin` from slipping between
    // them. `wal_checkpoint` reports lock contention in its result row rather than throwing, so
    // explicitly retry that row before resolving the write.
    await pRetry(
      async () => {
        const rows = await tursoDb.all("PRAGMA wal_checkpoint(TRUNCATE)");
        if (isWalCheckpointBusy(rows)) {
          if (!tursoDb.inTransaction) {
            // sync-wasm can retain a schema-change read lock after `ALTER TABLE` even after the
            // statement is finalized. Crossing an explicit transaction boundary releases that
            // stale lock; without it every later checkpoint remains busy for the life of the
            // connection. Raw calls are intentional here: the adapter lock is already held.
            await tursoDb.exec("BEGIN");
            await tursoDb.exec("COMMIT");
          }
          throw new Error("Local guest database WAL checkpoint is busy");
        }
      },
      {
        factor: 1,
        maxTimeout: CHECKPOINT_RETRY_DELAY_MS,
        minTimeout: CHECKPOINT_RETRY_DELAY_MS,
        retries: CHECKPOINT_RETRIES,
      },
    );
  };
  const connection = withSqliteBusyRetry(
    adaptTursoToExpoSqlite(
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- wasm Database is structurally compatible with the adapter's Turso surface
      tursoDb as never,
      databaseName,
      databaseName,
      lock,
      checkpointAfterStatement,
      false,
    ),
  );

  return {
    close: async () => {
      await tursoDb.close();
    },
    connection,
    databasePath: databaseName,
    updateSchema: (migrations) =>
      lock.runExclusive(() => updateSchema(migrations), {
        kind: "statement",
        queryText: "update schema",
      }),
  };
}
